import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Target,
  ShieldCheck,
  Handshake,
  FileText,
  Users,
  ChartLineUp,
  MapPin,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: 'easeOut' },
};

const Sobre = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-24 bg-white" data-testid="sobre-hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-bold text-[#2A2A2A] playfair tracking-tight mb-6">
              Sobre a RDC Advogados
            </h1>
            <p className="text-xl text-[#737373] leading-relaxed mb-4">
              Um escritório orientado por técnica, clareza e atuação responsável em demandas que exigem rigor jurídico e
              confiança institucional.
            </p>
            <p className="text-lg text-[#A98C67] font-semibold">
              Mais de 15 anos de experiência em advocacia trabalhista, de saúde, previdenciária e defesa em busca e apreensão
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 bg-[#FAFAFA]" data-testid="sobre-content-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeUp}>
              <p className="text-lg text-[#737373] leading-relaxed mb-6">
                A RDC Advogados atua com foco em Direito Trabalhista, ações contra plano de saúde, Direito
                Previdenciário com ênfase em BPC/LOAS e defesa em ações de busca e apreensão, desenvolvendo um atendimento pautado por análise criteriosa,
                comunicação objetiva e seriedade na condução de cada caso.
              </p>
              <p className="text-lg text-[#737373] leading-relaxed mb-6">
                Com mais de 15 anos de atuação, o escritório e seus advogados acumulam vasta experiência nas áreas de
                atendimento, consolidando uma trajetória marcada por comprometimento, conhecimento técnico aprofundado e
                resultados consistentes para os clientes.
              </p>
              <p className="text-lg text-[#737373] leading-relaxed mb-6">
                A identidade do escritório está vinculada a uma advocacia que valoriza o essencial: compreender
                profundamente a situação do cliente, avaliar tecnicamente os elementos do caso e indicar, com clareza,
                a medida juridicamente adequada. Em vez de uma comunicação excessivamente rebuscada ou distante, o
                escritório adota uma postura institucional firme, porém acessível, permitindo que o cliente compreenda
                sua realidade jurídica com mais segurança.
              </p>
              <p className="text-lg text-[#737373] leading-relaxed">
                Na RDC Advogados, cada demanda é tratada com a discrição e o rigor que o exercício da advocacia exige.
                A proposta do escritório é oferecer uma atuação séria, organizada e bem fundamentada, preservando a
                confiança como elemento central da relação profissional.
              </p>
            </motion.div>
            <motion.div {...fadeUp} transition={{ delay: 0.2, duration: 0.6 }}>
              <img
                src="https://customer-assets.emergentagent.com/job_bpc-loas/artifacts/9l5eob8q_WhatsApp%20Image%202026-03-24%20at%2019.39.14.jpeg"
                alt="Escritório RDC Advogados"
                className="rounded-sm shadow-lg w-full h-auto"
              />
              <p className="text-sm text-[#A98C67] mt-4 text-center font-medium">
                Escritório RDC Advogados - Centro do Rio de Janeiro
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-24 bg-white" data-testid="principles-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2A2A] playfair mb-4">
              Princípios de atuação
            </h2>
            <p className="text-lg text-[#737373] max-w-3xl mx-auto">
              Princípios que orientam o escritório
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Target,
                title: 'Precisão técnica',
                desc: 'Para que cada caso seja examinado com profundidade e responsabilidade',
              },
              {
                icon: FileText,
                title: 'Clareza na comunicação',
                desc: 'Para que o cliente compreenda o cenário jurídico sem ruídos ou excessos desnecessários',
              },
              {
                icon: ChartLineUp,
                title: 'Estratégia compatível com a realidade do caso',
                desc: 'Respeitando suas particularidades',
              },
              {
                icon: ShieldCheck,
                title: 'Postura institucional sóbria',
                desc: 'Alinhada à seriedade das matérias tratadas',
              },
              {
                icon: Users,
                title: 'Atendimento individualizado',
                desc: 'Com atenção concreta ao contexto de cada cliente',
              },
              {
                icon: Handshake,
                title: 'Confiança institucional',
                desc: 'Preservando a relação profissional com credibilidade',
              },
            ].map((item, index) => (
              <motion.div
                key={item.title || item.id || index}
                {...fadeUp}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="bg-white border border-[#2A2A2A]/10 rounded-sm p-8"
                data-testid={`principle-${index}`}
              >
                <item.icon size={40} weight="duotone" className="text-[#A98C67] mb-4" />
                <h3 className="text-xl font-semibold text-[#2A2A2A] playfair mb-3">{item.title}</h3>
                <p className="text-[#737373] leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How we work */}
      <section className="py-24 bg-[#FAFAFA]" data-testid="how-we-work-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2A2A] playfair mb-4">
              Como o escritório atua
            </h2>
            <p className="text-lg text-[#737373] max-w-3xl mx-auto">
              Uma advocacia conduzida com método e critério
            </p>
          </motion.div>

          <motion.div {...fadeUp} className="max-w-4xl mx-auto">
            <div className="bg-white border border-[#2A2A2A]/10 rounded-sm p-8 md:p-12">
              <p className="text-lg text-[#737373] leading-relaxed mb-6">
                O atendimento na RDC Advogados começa pela compreensão da situação apresentada e pela análise dos
                documentos disponíveis. A partir daí, o escritório identifica os pontos juridicamente relevantes,
                orienta o cliente de forma clara sobre riscos e possibilidades e define a estratégia adequada para a
                condução do caso.
              </p>
              <p className="text-lg text-[#737373] leading-relaxed">
                A atuação pode envolver providências administrativas, análise de documentos, preparação técnica da demanda
                e adoção de medidas judiciais, quando cabíveis. Em todas as etapas, o compromisso é o mesmo: seriedade,
                organização e clareza na defesa de direitos.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Location */}
      <section className="py-24 bg-white" data-testid="location-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeUp}>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2A2A] playfair mb-6">
                Presença institucional
              </h2>
              <p className="text-xl text-[#737373] mb-6 leading-relaxed">
                Localização estratégica no Centro do Rio de Janeiro
              </p>
              <p className="text-lg text-[#737373] leading-relaxed mb-6">
                A RDC Advogados está localizada na Rua São José, nº 46, Grupo 506/507, Centro - Rio de Janeiro/RJ, CEP:
                20.010-020, em região de fácil acesso e com forte presença institucional.
              </p>
              <p className="text-lg text-[#737373] leading-relaxed">
                A localização no Centro do Rio de Janeiro reforça a atuação do escritório em uma área tradicionalmente
                ligada à atividade jurídica e empresarial.
              </p>
            </motion.div>
            <motion.div {...fadeUp} transition={{ delay: 0.2, duration: 0.6 }}>
              <div className="bg-[#2A2A2A] text-white rounded-sm p-8">
                <MapPin size={48} weight="duotone" className="mb-6" />
                <h3 className="text-2xl font-bold playfair mb-4">Endereço</h3>
                <p className="text-lg leading-relaxed">
                  Rua São José, nº 46<br />
                  Grupo 506/507<br />
                  Centro - Rio de Janeiro/RJ<br />
                  CEP: 20.010-020
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#2A2A2A] text-white" data-testid="sobre-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl sm:text-4xl font-bold playfair mb-6">
              Fale com a RDC Advogados
            </h2>
            <p className="text-lg text-white/80 mb-8">
              Se você busca orientação jurídica com mais clareza, segurança e rigor técnico, entre em contato para
              apresentar a sua situação.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contato">
                <Button
                  size="lg"
                  data-testid="sobre-contact-button"
                  className="bg-white text-[#2A2A2A] hover:bg-white/90 rounded-sm font-medium px-8 py-6 text-base"
                >
                  Solicitar análise
                </Button>
              </Link>
              <a href="https://wa.me/552128880258" target="_blank" rel="noopener noreferrer">
                <Button
                  size="lg"
                  
                  data-testid="sobre-whatsapp-button"
                  className="bg-[#25D366] text-white hover:bg-[#20BA5A] border-0 rounded-sm font-medium px-8 py-6 text-base"
                >
                  Entrar em contato
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Sobre;